import React, { memo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import "../styles/LocationPickerModal.css";

interface LocationPickerModalProps {
  onClose: () => void;
  onLocationSelected: (location: {
    latitude: number;
    longitude: number;
  }) => void;
  initialLocation?: { latitude: number; longitude: number };
}

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

// Center of Russia (approximately Moscow area)
const defaultCenter = {
  lat: 55.7558,
  lng: 37.6173,
};

const defaultZoom = 4; // Zoomed out to show more of Russia

// Zoom applied when a location is set via the coordinate input (or initial).
const focusedZoom = 15;

const formatCoordinates = (lat: number, lng: number): string =>
  `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

// Parse pasted/typed coordinates. Accepts "lat, lng" with any separator
// (comma, space, semicolon) by extracting the first two signed numbers, and
// validates the lat/lng ranges. Returns null when it can't form a valid pair.
export const parseCoordinates = (
  value: string
): { lat: number; lng: number } | null => {
  const numbers = value.match(/-?\d+(?:\.\d+)?/g);
  if (!numbers || numbers.length < 2) return null;

  const lat = parseFloat(numbers[0]);
  const lng = parseFloat(numbers[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

  return { lat, lng };
};

const LocationPickerModal: React.FC<LocationPickerModalProps> = memo(
  ({ onClose, onLocationSelected, initialLocation }) => {
    const { t } = useTranslation();
    const { isLoaded } = useJsApiLoader({
      id: "google-map-script",
      googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    });

    const [selectedLocation, setSelectedLocation] = useState<{
      lat: number;
      lng: number;
    } | null>(
      initialLocation
        ? { lat: initialLocation.latitude, lng: initialLocation.longitude }
        : null
    );

    const [center, setCenter] = useState(
      initialLocation
        ? { lat: initialLocation.latitude, lng: initialLocation.longitude }
        : defaultCenter
    );

    const [zoom, setZoom] = useState(initialLocation ? 13 : defaultZoom);

    // Text in the coordinate input, kept in sync with the pin both ways.
    const [coordsInput, setCoordsInput] = useState(
      initialLocation
        ? formatCoordinates(
            initialLocation.latitude,
            initialLocation.longitude
          )
        : ""
    );
    const [coordsError, setCoordsError] = useState(false);

    // Try to get user's current location on mount (but don't auto-select it)
    useEffect(() => {
      if (!initialLocation && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const newCenter = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setCenter(newCenter);
            setZoom(13);
            // Don't auto-select, let user click on map
          },
          (error) => {
            console.error("Error getting location:", error);
            // Stay with default Russia center
          }
        );
      }
    }, [initialLocation]);

    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const newLocation = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };
        setSelectedLocation(newLocation);
        // Reflect the clicked point in the coordinate input.
        setCoordsInput(formatCoordinates(newLocation.lat, newLocation.lng));
        setCoordsError(false);
      }
    }, []);

    // Paste/type coordinates -> drop the pin and recenter the map onto it.
    const handleCoordsInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCoordsInput(value);

        if (!value.trim()) {
          setCoordsError(false);
          return;
        }

        const parsed = parseCoordinates(value);
        if (!parsed) {
          setCoordsError(true);
          return;
        }

        setCoordsError(false);
        setSelectedLocation(parsed);
        setCenter(parsed);
        setZoom((current) => Math.max(current, focusedZoom));
      },
      []
    );

    const handleDone = useCallback(() => {
      if (selectedLocation) {
        onLocationSelected({
          latitude: selectedLocation.lat,
          longitude: selectedLocation.lng,
        });
        onClose();
      }
    }, [selectedLocation, onLocationSelected, onClose]);

    const handleOverlayClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      },
      [onClose]
    );

    if (!isLoaded) {
      return (
        <div className="location-picker-overlay">
          <div className="location-picker-modal">
            <div className="location-picker-loading">
              <p>{t("map.loadingMap")}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="location-picker-overlay" onClick={handleOverlayClick}>
        <div className="location-picker-modal">
          <div className="location-picker-header">
            <h3>{t("report.selectLocation")}</h3>
            <button className="location-picker-close" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="location-picker-instructions">
            <p>{t("report.clickOnMap")}</p>
            <div className="location-picker-coords">
              <label className="coords-label" htmlFor="location-picker-coords">
                {t("report.orEnterCoordinates")}
              </label>
              <input
                id="location-picker-coords"
                type="text"
                className={`coords-input${coordsError ? " has-error" : ""}`}
                value={coordsInput}
                onChange={handleCoordsInputChange}
                placeholder={t("report.coordinatesPlaceholder")}
                aria-label={t("report.coordinates")}
                aria-invalid={coordsError}
                autoComplete="off"
                spellCheck={false}
              />
              {coordsError && (
                <span className="coords-error" role="alert">
                  {t("report.invalidCoordinates")}
                </span>
              )}
            </div>
          </div>

          <div className="location-picker-map">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={zoom}
              onClick={handleMapClick}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
            >
              {selectedLocation && (
                <Marker
                  position={selectedLocation}
                  animation={google.maps.Animation.DROP}
                />
              )}
            </GoogleMap>
          </div>

          <div className="location-picker-footer">
            <button className="location-picker-cancel" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              className="location-picker-done"
              onClick={handleDone}
              disabled={!selectedLocation}
            >
              {t("common.done")}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

LocationPickerModal.displayName = "LocationPickerModal";

export default LocationPickerModal;
